/**
 * SearchableSelect - Simple combobox with search/filter.
 *
 * Default: inline `absolute` menu (no Portal) — keeps it inside Dialog focus
 * traps with zero ceremony. Opt-in `portal` renders the menu into the nearest
 * Radix Dialog content (or <body> fallback) so it can escape a scroll /
 * `overflow-hidden` ancestor without being clipped, while staying inside the
 * dialog's focus + dismiss layer.
 */

import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

export type SearchableSelectOption = {
  value: string;
  label: string;
  description?: string;
};

/** Gap between the trigger and its menu. */
const MENU_GAP = 4;
/** Breathing room kept between the menu and the viewport / host edge. */
const VIEWPORT_MARGIN = 8;
/**
 * Below this much room under the trigger the menu opens UPWARD instead.
 * Roughly search row + a few options + the count row — less than this and the
 * menu is technically visible but not usable.
 */
const MENU_MIN_HEIGHT = 220;

type SearchableSelectProps = {
  /** Currently selected value */
  value?: string;
  /** Callback when value changes */
  onValueChange: (value: string) => void;
  /** Available options */
  options: SearchableSelectOption[];
  /** Placeholder when no value selected */
  placeholder?: string;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Custom class for trigger button */
  className?: string;
  /** Test ID for the component */
  'data-testid'?: string;
  /** The id of the visible label that names the trigger (a form field's `<Label>`). */
  'aria-labelledby'?: string;
  /**
   * When true, render the dropdown in a `document.body` portal with fixed
   * positioning anchored to the trigger. Use inside scroll/`overflow-hidden`
   * containers (e.g. a bounded Dialog) where the default inline `absolute`
   * menu would be clipped. Default false preserves the inline behaviour for
   * all existing call sites.
   */
  portal?: boolean;
};

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  className,
  'data-testid': testId,
  'aria-labelledby': ariaLabelledBy,
  portal = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuPos, setMenuPos] = useState<{
    /** Set when the menu opens downward; mutually exclusive with `bottom`. */
    top?: number;
    /** Set when the menu flipped upward; anchoring by `bottom` rather than
     *  `top` means we never have to measure the menu's own height. */
    bottom?: number;
    left: number;
    width: number;
    /** Room available in the chosen direction — the options list shrinks to fit. */
    maxHeight: number;
  } | null>(null);
  // Portal target: the nearest Radix Dialog content (so the menu stays
  // inside the dialog's focus-trap + dismiss layer — clicking/typing in it
  // won't close the dialog), falling back to <body> for non-dialog hosts.
  // When the host is the dialog the menu is positioned `absolute` relative
  // to it (Radix DialogContent has a transform → it's the containing block);
  // for the <body> fallback it's `fixed` in viewport coords.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const portalInDialog =
    portalTarget != null && portalTarget !== document.body;
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const query = search.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.value.toLowerCase().includes(query) ||
        (opt.description && opt.description.toLowerCase().includes(query))
    );
  }, [options, search]);

  // Get selected option label
  const selectedLabel = useMemo(() => {
    const selected = options.find((opt) => opt.value === value);
    return selected?.label;
  }, [options, value]);

  // Reset state when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearch('');
      setHighlightedIndex(0);
    }
  }, [open]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions.length]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (open) {
      // Small delay to ensure DOM is rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // With `portal`, the menu lives outside containerRef (in a body
      // portal) — must also treat clicks inside it as "inside" so option
      // selection isn't swallowed by the close-on-mousedown.
      const insideTrigger = containerRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideTrigger && !insideMenu) {
        setOpen(false);
      }
    };

    // Use capture phase to catch clicks before they're stopped
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [open]);

  // Global Escape key handler to prevent Dialog from closing
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        setOpen(false);
      }
    };

    // Use capture phase to catch the event before Radix Dialog sees it
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [open]);

  // Scroll the highlighted item into view WITHIN THE OPTIONS LIST ONLY.
  //
  // `item.scrollIntoView()` walks every scrollable ancestor, not just the list.
  // In a non-portal menu whose host dialog scrolls its own body (the Data and
  // Schema Compare setup dialogs both do — `flex-1 min-h-0 overflow-y-auto`),
  // the menu hangs below the trigger and past that body's clip rect, so the
  // first ArrowDown scrolled THE DIALOG to reveal the option. That slid the
  // menu under a cursor still parked on the trigger from the opening click,
  // fired `onMouseEnter` on whichever option landed there, and snapped the
  // keyboard highlight straight back — arrow keys appeared dead.
  //
  // Scrolling the list by hand keeps the effect where it belongs. Rects rather
  // than `offsetTop`: the list is statically positioned, so its children
  // measure against whichever ancestor happens to be positioned.
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    const item = list?.children[highlightedIndex] as HTMLElement | undefined;
    if (!list || !item) return;
    const listRect = list.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    if (itemRect.top < listRect.top) {
      list.scrollTop -= listRect.top - itemRect.top;
    } else if (itemRect.bottom > listRect.bottom) {
      list.scrollTop += itemRect.bottom - listRect.bottom;
    }
  }, [highlightedIndex, open]);

  const handleSelect = useCallback(
    (selectedValue: string) => {
      onValueChange(selectedValue);
      setOpen(false);
    },
    [onValueChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            Math.min(prev + 1, filteredOptions.length - 1)
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredOptions[highlightedIndex]) {
            handleSelect(filteredOptions[highlightedIndex].value);
          }
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          // Use native event to stop it from reaching the Dialog
          e.nativeEvent.stopImmediatePropagation();
          setOpen(false);
          break;
        case 'Tab':
          setOpen(false);
          break;
      }
    },
    [filteredOptions, highlightedIndex, handleSelect]
  );

  const handleTriggerClick = useCallback(() => {
    if (!disabled) {
      setOpen((prev) => !prev);
    }
  }, [disabled]);

  // Portal mode: anchor the menu to the trigger rect — above or below,
  // whichever has room — and keep it glued during scroll/resize. containerRef
  // is w-full and the menu is absolute, so its rect == the trigger's rect.
  useLayoutEffect(() => {
    if (!portal || !open) return;
    const host =
      (containerRef.current?.closest(
        '[role="dialog"]',
      ) as HTMLElement | null) ?? document.body;
    setPortalTarget(host);
    const update = () => {
      const r = containerRef.current?.getBoundingClientRect();
      if (!r) return;
      // Escaping the clip rect isn't enough on its own: the pickers that need
      // portal mode tend to be the LAST field in a tall panel, so "below the
      // trigger" is often off-screen. Flip above when that's the better side,
      // and hand the menu the room it actually has so the list scrolls instead
      // of running past the edge.
      const spaceBelow = window.innerHeight - r.bottom - MENU_GAP - VIEWPORT_MARGIN;
      const spaceAbove = r.top - MENU_GAP - VIEWPORT_MARGIN;
      const flip = spaceBelow < MENU_MIN_HEIGHT && spaceAbove > spaceBelow;
      const maxHeight = Math.max(MENU_MIN_HEIGHT, flip ? spaceAbove : spaceBelow);
      // <body> fallback positions in viewport coords; inside a dialog the
      // (transformed) dialog box is the containing block, so offsets are
      // relative to it instead.
      const h = host === document.body ? null : host.getBoundingClientRect();
      const bottomEdge = h ? h.bottom : window.innerHeight;
      setMenuPos({
        ...(flip
          ? { bottom: bottomEdge - r.top + MENU_GAP }
          : { top: r.bottom - (h?.top ?? 0) + MENU_GAP }),
        left: r.left - (h?.left ?? 0),
        width: r.width,
        maxHeight,
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [portal, open]);

  return (
    <div ref={containerRef} className="relative w-full">
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-labelledby={ariaLabelledBy}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={handleTriggerClick}
        className={cn(
          'w-full justify-between font-normal',
          !value && 'text-muted-foreground',
          className
        )}
        data-testid={testId}
      >
        <span className="truncate">{selectedLabel ?? placeholder}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open &&
        (() => {
          const menuChrome = cn(
            'rounded-md border bg-popover text-popover-foreground shadow-md',
            'animate-in fade-in-0 zoom-in-95'
          );
          const menuBody = (
          // `min-h-0` + the `shrink-0` chrome rows below: in portal mode the
          // wrapper carries a `maxHeight`, and this is what routes the
          // resulting shrinkage into the options list rather than squashing
          // the search box and the count row. No-ops in the inline menu,
          // whose wrapper has no height constraint to distribute.
          <div className="flex min-h-0 flex-col">
            {/* Search input */}
            <div className="flex shrink-0 items-center border-b px-3 py-2">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-searchable-select-input
              />
            </div>

            {/* Options list */}
            <div
              ref={listRef}
              className="max-h-[200px] min-h-0 overflow-y-auto p-1"
              role="listbox"
            >
              {filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No matches found
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <div
                    key={option.value}
                    role="option"
                    aria-selected={value === option.value}
                    tabIndex={-1}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                      'hover:bg-accent hover:text-accent-foreground',
                      value === option.value && 'bg-accent/50',
                      highlightedIndex === index &&
                        'bg-accent text-accent-foreground'
                    )}
                    onClick={() => handleSelect(option.value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    data-selected={highlightedIndex === index}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                    {option.description && (
                      <span className="ml-2 truncate text-xs text-muted-foreground">
                        {option.description}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Count indicator */}
            {options.length > 0 && (
              <div className="shrink-0 border-t px-3 py-1.5 text-xs text-muted-foreground">
                {search && filteredOptions.length !== options.length
                  ? `${filteredOptions.length} of ${options.length}`
                  : `${options.length} ${options.length === 1 ? 'option' : 'options'}`}
              </div>
            )}
          </div>
          );

          if (portal) {
            // portalTarget resolves in a layout effect — skip the first
            // paint rather than briefly flashing the clipped inline menu.
            if (!portalTarget) return null;
            return createPortal(
              <div
                ref={menuRef}
                className={cn(
                  portalInDialog ? 'absolute' : 'fixed',
                  'z-[60] flex flex-col overflow-hidden',
                  menuChrome,
                )}
                style={{
                  top: menuPos?.top,
                  bottom: menuPos?.bottom,
                  left: menuPos?.left ?? -9999,
                  width: menuPos?.width,
                  maxHeight: menuPos?.maxHeight,
                  // <body> fallback only: Radix modal Dialog disables pointer
                  // events outside its content, so a body-portaled menu must
                  // opt back in. (No-op when portaled into the dialog.)
                  pointerEvents: 'auto',
                }}
                // Defensive shield for the <body> fallback: stop these from
                // bubbling to a host dialog's document-level dismiss/focus
                // listeners. Harmless when portaled inside the dialog.
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {menuBody}
              </div>,
              portalTarget,
            );
          }

          return (
            <div
              ref={menuRef}
              className={cn('absolute left-0 top-full z-50 mt-1 w-full', menuChrome)}
            >
              {menuBody}
            </div>
          );
        })()}
    </div>
  );
}
